import {
  FlashOn as FlashIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  AutoFixHigh as EnrichIcon,
  Assignment as AssignIcon,
  Campaign as CampaignIcon,
  Language as HttpIcon,
  Webhook as WebhookIcon,
  AdsClick as MoveToTALIcon,
  LinkedIn as LinkedInIcon,
  Psychology as OpenAIIcon,
  SmartToy as AnthropicIcon,
  AccountTree as SwitchIcon,
  MergeType as MergeIcon,
  AddAlarm as DelayIcon,
  Code as CodeIcon,
  ManageSearch as DatabaseSearchIcon,
  DataObject as DefineVarsIcon,
} from '@mui/icons-material';
// Import SVG icons as React components
// TODO: Fix SVG imports - using fallback icons for now
// import { ReactComponent as LLMIcon } from '../../../assets/icons/llm-node-icon.svg';
// import { ReactComponent as HPMIcon } from '../../../assets/icons/hpm-node-icon.svg';
// import { ReactComponent as SheetIcon } from '../../../assets/icons/google-sheet-icon.svg';

// Fallback icons until SVG imports are fixed
const LLMIcon = OpenAIIcon;
const HPMIcon = PersonIcon;
const SheetIcon = BusinessIcon;
import type { Node } from '@xyflow/react';
import { NodeData } from '../types';

// add new node types here also add their order in the DesiredNodeOrder array below NODES_DATA
export const NODES_DATA: Record<string, NodeData> = {
  httpNode: {
    title: 'HTTP Request',
    icon: HttpIcon,
    statusChecker: (data: any) => {
      // Check if the node has a URL parameter configured (in parameters or direct)
      return Boolean(
        (data.parameters?.url && data.parameters.url.trim() !== '') ||
          (data.url && data.url.trim() !== ''),
      );
    },
    type: 'trigger',
    subHeader: 'Makes an HTTP request and returns the response.',
    nodeColor: 'red',
  },
  webhookNode: {
    title: 'Webhook',
    icon: WebhookIcon,
    statusChecker: (data: any) => {
      // Check if webhook has required configuration
      const hasHttpMethods =
        data.httpMethod && Array.isArray(data.httpMethod) && data.httpMethod.length > 0;
      const hasAuthType = data.authentication && data.authentication.type;
      const hasResponseMode = data.responseMode;

      return Boolean(hasHttpMethods && hasAuthType && hasResponseMode);
    },
    type: 'trigger',
    subHeader: 'Receive HTTP requests and trigger workflows via webhook URLs.',
    nodeColor: 'red',
  },
  manualTrigger: {
    title: 'Manual Trigger',
    icon: FlashIcon,
    statusChecker: (data: any) => {
      // Manual trigger is configured if it has been explicitly saved or has parameters
      return Boolean(
        data.configured || (data.parameters && Object.keys(data.parameters).length > 0),
      );
    },
    subHeader: 'Manually trigger workflows on demand.',
  },
  scheduleTrigger: {
    title: 'Schedule Trigger',
    icon: '⏰',
    statusChecker: (data: any) => {
      // Schedule trigger is configured if it has been saved or has meaningful schedule parameters
      const params = data.parameters || {};
      return Boolean(
        data.configured ||
          params.scheduleType ||
          params.cronExpression ||
          params.intervalValue ||
          params.executeAt,
      );
    },
    subHeader: 'Trigger workflow on a schedule (cron, interval, or once)',
  },
  gmailTrigger: {
    title: 'Gmail Trigger',
    icon: PersonIcon,
    statusChecker: (data: any) => Boolean(data?.labelIds),
    type: 'trigger',
    subHeader: 'Trigger on new Gmail messages.',
    nodeColor: 'red',
  },
  conditionNode: {
    title: 'Condition',
    icon: FlashIcon,
    statusChecker: (data: any) => Boolean(data?.field),
    subHeader: 'Add conditional logic to branch workflows based on rules.',
    hideHandleBottom: true,
  },
  mergeNode: {
    title: 'Merge',
    icon: MergeIcon,
    statusChecker: (data: any) => {
      // Node is configured when parameters are set (mode is auto-set to 'combine')
      return Boolean(data.parameters?.mode || data.parameters?.numberOfInputs);
    },
    subHeader: 'Merge data from multiple sources into a single output.',
    nodeColor: 'teal',
  },
  googleSheetNode: {
    title: 'Google Sheet',
    icon: SheetIcon,
    statusChecker: (data: any) => {
      // Check if the node has required parameters for Google Sheets
      const params = data.parameters || data;
      return Boolean(params.credentialId && params.spreadsheetId && params.sheetName);
    },
    type: 'trigger',
    subHeader: 'Connects to Google Sheets to read or write rows.',
    nodeColor: 'green',
  },
  googleSheetsTrigger: {
    title: 'Google Sheet Trigger',
    icon: SheetIcon,
    statusChecker: (data: any) => {
      // Check if the trigger node has required parameters
      const params = data.parameters || data;
      return Boolean(
        params.credentialId && params.spreadsheetId && params.sheetName && params.triggerType,
      );
    },
    type: 'trigger',
    subHeader: 'Connects to Google Sheets to read or write rows.',
    nodeColor: 'green',
  },
  llmNode: {
    title: 'LLM',
    icon: LLMIcon,
    statusChecker: (data: any) => Boolean(data?.parameters?.model),
    subHeader: 'Generate responses or process text using a language model.',
    nodeColor: 'indigo',
  },
  hpmNode: {
    title: 'Generated Personalized Message',
    icon: HPMIcon,
    statusChecker: (data: any) => {
      // Node is configured if user has selected at least email or linkedin (stored in parameters)
      return Boolean(data.parameters?.email || data.parameters?.linkedin);
    },
    subHeader: 'Send a highly tailored, personalized message to engage contacts.',
    nodeColor: 'indigo',
  },
  enrichDomainNode: {
    title: 'Person Linkedin Lookup',
    icon: EnrichIcon,
    statusChecker: (data: any) => {
      // Required: Company Domain AND First name
      const hasCompanyDomain = Boolean(
        data.parameters?.companyDomain?.staticValue ||
          data.parameters?.companyDomain?.dynamicMapping ||
          data.parameters?.domain ||
          data.domain,
      );
      const hasFirstName = Boolean(
        data.parameters?.firstName?.staticValue ||
          data.parameters?.firstName?.dynamicMapping ||
          data.parameters?.name ||
          data.firstName ||
          data.name ||
          data.parameters?.firstName,
      );
      return hasCompanyDomain && hasFirstName;
    },
    subHeader: 'This node finds LinkedIn profile information for a person.',
    nodeColor: 'orange',
  },
  moveToTALNode: {
    title: 'Add Contact to TAL',
    icon: MoveToTALIcon,
    statusChecker: (data: any) => {
      const params = data.parameters || {};
      if (Array.isArray(params.dynamicFields)) {
        let domain = '', email = '';
        params.dynamicFields.forEach((f: any) => {
          if (f.entityName === 'Domain' || f.entityName === 'domain')
            domain = f.value?.trim() || '';
          if (f.entityName === 'Corporate Email ID' || f.entityName === 'email')
            email = f.value?.trim() || '';
        });
        return !!(domain || email);
      }
      return Boolean(params.domain || params.email);
    },
    subHeader: 'Add targeted contacts to Sprouts TAL',
  },
  moveToAccountTALNode: {
    title: 'Add Account to TAL',
    icon: MoveToTALIcon,
    statusChecker: (data: any) => {
      const params = data.parameters || {};
      if (Array.isArray(params.dynamicFields)) {
        let domain = '';
        params.dynamicFields.forEach((f: any) => {
          if (f.entityName === 'Domain' || f.entityName === 'domain')
            domain = f.value?.trim() || '';
        });
        return !!domain;
      }
      return Boolean(
        params.companyDomain?.staticValue ||
          params.companyDomain?.dynamicMapping ||
          params.domain ||
          data.domain,
      );
    },
    subHeader: 'Add targeted accounts to Sprouts TAL',
  },
  criteriaAccountNode: {
    title: 'Account Criteria',
    icon: BusinessIcon,
    statusChecker: (data: any) => {
      // Check if filters have been configured
      const filters = data.parameters?.filters?.value || {};
      return Object.keys(filters).length > 0;
    },
    subHeader: 'Define rules to filter accounts for workflows or actions.',
  },
  criteriaContactNode: {
    title: 'Contact Criteria',
    icon: PersonIcon,
    statusChecker: (data: any) => {
      // Check if filters have been configured
      const filters = data.parameters?.filters?.value || {};
      return Object.keys(filters).length > 0;
    },
    subHeader: 'Define rules to filter contacts for workflows or actions.',
  },
  sourceContactNode: {
    title: 'Source Contacts',
    icon: PersonIcon,
    statusChecker: (data: any) => {
      // Check if source contacts filters have been configured in parameters
      const sourceContactsFilters = data.parameters?.sourceContactsFilters?.value || {};
      return Object.keys(sourceContactsFilters).length > 0;
    },
    subHeader: 'Pull relevant contacts from the database for workflows.',
  },
  enrichContactNode: {
    title: 'Enrich Contact',
    icon: EnrichIcon,
    statusChecker: (data: any) => {
      // Check if at least one enrichment option is enabled in parameters
      const params = data.parameters || {};
      const enrichWithEmail = params.enrichWithEmail?.staticValue ?? params.enrichWithEmail;
      const enrichWithPhone = params.enrichWithPhone?.staticValue ?? params.enrichWithPhone;
      return Boolean(enrichWithEmail || enrichWithPhone);
    },
    subHeader: 'Enrich contact data with additional information.',
    nodeColor: 'blue',
  },
  enrichContact: {
    title: 'Enrich Contact',
    icon: EnrichIcon,
    statusChecker: (data: any) => {
      // Check if at least one enrichment option is enabled in parameters
      const params = data.parameters || {};
      const enrichWithEmail = params.enrichWithEmail?.staticValue ?? params.enrichWithEmail;
      const enrichWithPhone = params.enrichWithPhone?.staticValue ?? params.enrichWithPhone;
      return Boolean(enrichWithEmail || enrichWithPhone);
    },
    subHeader: 'Enrich contact data with additional information.',
    nodeColor: 'blue',
  },
  assignContactNode: {
    title: 'Assign Contact Owner',
    icon: AssignIcon,
    statusChecker: (data: any) => {
      // Check both legacy selectedOwners and new parameter format
      if (Array.isArray(data.selectedOwners) && data.selectedOwners.length > 0) {
        return true;
      }

      // Check new parameter format
      const assignTo = data.parameters?.assignTo;
      if (
        assignTo?.mode === 'static' &&
        Array.isArray(assignTo.staticValue) &&
        assignTo.staticValue.length > 0
      ) {
        return true;
      }
      if (assignTo?.mode === 'dynamic' && assignTo.dynamicMapping) {
        return true;
      }

      return false;
    },
    subHeader: 'Assign a contact to its owner for follow-ups or management.',
  },
  databaseSearchNode: {
    title: 'Database Search',
    icon: DatabaseSearchIcon,
    statusChecker: (data: any) => {
      const params = data.parameters || {};
      const entityType: string = params.entityType || '';
      if (!entityType) return false;
      if (entityType === 'account') {
        return Boolean(params.domain || params.companyLinkedinUrl);
      }
      // contact: linkedin OR (domain + at least one name field)
      if (params.contactLinkedinUrl) return true;
      return Boolean(params.domain && (params.firstName || params.lastName));
    },
    subHeader: 'Search MDQS database for matching account or contact records.',
  },
  addToCampaignNode: {
    title: 'Add to Campaign',
    icon: CampaignIcon,
    statusChecker: (data: any) => Boolean(data.selectedCMP && data.selectedSequence),
    subHeader: 'Add contacts to marketing campaigns.',
    nodeColor: 'purple',
  },
  addToCampaign: {
    title: 'Add to Campaign',
    icon: CampaignIcon,
    statusChecker: (data: any) => Boolean(data.selectedCMP && data.selectedSequence),
    subHeader: 'Add contacts to marketing campaigns.',
    nodeColor: 'purple',
  },
  accountCreatedNode: {
    title: 'Account Created',
    icon: BusinessIcon,
    statusChecker: (data: any) => {
      // Platform trigger is configured if it has been saved or has templateType parameter
      const hasTemplateType = Boolean(data.parameters?.templateType);
      return Boolean(data.configured || hasTemplateType);
    },
    type: 'only-trigger',
    subHeader: 'Triggered when a new account is created.',
  },
  accountUpdatedNode: {
    title: 'Account Updated',
    icon: BusinessIcon,
    statusChecker: (data: any) => {
      // Platform trigger is configured if it has been saved or has templateType parameter
      const hasTemplateType = Boolean(data.parameters?.templateType);
      return Boolean(data.configured || hasTemplateType);
    },
    type: 'only-trigger',
    subHeader: 'Triggered when account details are updated.',
  },
  contactCreatedNode: {
    title: 'Contact Created',
    icon: PersonIcon,
    statusChecker: (data: any) => {
      // Platform trigger is configured if it has been saved or has templateType parameter
      const hasTemplateType = Boolean(data.parameters?.templateType);
      return Boolean(data.configured || hasTemplateType);
    },
    type: 'only-trigger',
    subHeader: 'Triggered when a new contact is added.',
  },
  contactCreated: {
    title: 'Contact Created',
    icon: PersonIcon,
    statusChecker: (data: any) => {
      // Platform trigger is configured if it has been saved or has templateType parameter
      const hasTemplateType = Boolean(data.parameters?.templateType);
      return Boolean(data.configured || hasTemplateType);
    },
    type: 'only-trigger',
    subHeader: 'Triggered when a new contact is added.',
  },
  contactUpdatedNode: {
    title: 'Contact Updated',
    icon: PersonIcon,
    statusChecker: (data: any) => {
      // Platform trigger is configured if it has been saved or has templateType parameter
      const hasTemplateType = Boolean(data.parameters?.templateType);
      return Boolean(data.configured || hasTemplateType);
    },
    type: 'only-trigger',
    subHeader: "Triggered when a contact's information is modified.",
  },
  // Logic nodes
  if: {
    title: 'IF',
    icon: FlashIcon,
    statusChecker: (data: any) => {
      // Check if conditions are configured and at least one condition has required fields
      const conditions = data.parameters?.conditions || [];
      return (
        conditions.length > 0 &&
        conditions.some(
          (condition: any) => condition.field && condition.operator && condition.value,
        )
      );
    },
    subHeader: 'Split workflow based on conditions.',
    nodeColor: 'orange',
  },
  set: {
    title: 'Set',
    icon: FlashIcon,
    statusChecker: () => true,
    subHeader: 'Set a value for a specific field.',
    nodeColor: 'purple',
  },
  httpRequest: {
    title: 'HTTP Request',
    icon: HttpIcon,
    statusChecker: (data: any) => {
      // Check if the node has a URL parameter configured
      return Boolean(data.parameters?.url && data.parameters.url.trim() !== '');
    },
    subHeader: 'Make HTTP requests to external APIs or services.',
    nodeColor: 'red',
  },
  gmail: {
    title: 'Gmail',
    icon: PersonIcon,
    statusChecker: (data: any) => Boolean((data as any)?.parameters?.credentialId),
    subHeader: 'Send or receive emails using Gmail.',
    nodeColor: 'blue',
  },
  slack: {
    title: 'Slack',
    icon: PersonIcon,
    statusChecker: (data: any) => {
      // Check if required fields are configured in parameters
      return Boolean(data.parameters?.channel && data.parameters?.credentialId);
    },
    subHeader: 'Send messages or notifications via Slack.',
    nodeColor: 'blue',
  },
  teams: {
    title: 'Microsoft Teams',
    icon: PersonIcon,
    statusChecker: (data: any) => Boolean((data as any)?.channel),
    subHeader: 'Send messages or notifications via Microsoft Teams.',
    nodeColor: 'blue',
  },
  googleSheets: {
    title: 'Google Sheets',
    icon: SheetIcon,
    statusChecker: (data: any) => {
      // Check if the Google Sheets node has required parameters
      const params = data.parameters || data;
      return Boolean(
        params.credentialId && params.spreadsheetId && params.sheetName && params.operation,
      );
    },
    subHeader: 'Read from or write to Google Sheets.',
    nodeColor: 'green',
  },
  generateMessage: {
    title: 'Generate Personalized Message',
    icon: HPMIcon,
    statusChecker: (data: any) => {
      // Check if required parameters are configured (they are stored as objects with type and value)
      const params = data.parameters || {};
      const hasContactId = Boolean(params.contactId?.value || params.contactId?.field);
      const hasMessageType = Boolean(params.messageType?.value || params.messageType?.field);
      return hasContactId && hasMessageType;
    },
    subHeader: 'Create tailored messages to engage your audience effectively.',
    nodeColor: 'indigo',
  },
  // data nodes
  mapFields: {
    title: 'Map Fields',
    icon: FlashIcon,
    statusChecker: (data: any) =>
      Boolean(
        (data?.parameters &&
          Array.isArray(data.parameters?.fieldMappings) &&
          data.parameters.fieldMappings.length > 0) ||
          (data as any)?.fieldMappings,
      ),
    subHeader: 'Map fields from one format to another.',
    nodeColor: 'teal',
  },
  merge: {
    title: 'Merge',
    icon: MergeIcon,
    statusChecker: () => true,
    subHeader: 'Combine data from multiple sources.',
    nodeColor: 'brown',
  },
  wait: {
    title: 'Wait',
    icon: DelayIcon,
    statusChecker: (data: any) => Boolean(data.parameters?.waitMinutes),
    subHeader: 'Pause workflow execution for a defined number of minutes.',
    nodeColor: 'blue',
  },
  addAccountsByICP: {
    title: 'Discover Accounts',
    icon: MoveToTALIcon,
    statusChecker: (data: any) => Boolean(data.parameters?.icpPrimaryKey),
    subHeader: 'Add accounts to TAL using an ICP filter.',
    nodeColor: 'indigo',
  },
  condition: {
    title: 'Condition',
    icon: FlashIcon,
    statusChecker: (data: any) =>
      Boolean((data as any)?.field || (data?.parameters && (data.parameters as any)?.field)),
    subHeader: 'Add conditional logic to branch workflows based on rules.',
    nodeColor: 'orange',
  },
  linkedinSearchNode: {
    title: 'Linkedin Search',
    icon: LinkedInIcon,
    statusChecker: () => true,
    subHeader: 'Search and find relevant contacts on LinkedIn.',
    nodeColor: 'orange',
  },
  // AI nodes
  openai: {
    title: 'OpenAI',
    icon: OpenAIIcon,
    statusChecker: (data: any) => Boolean(data?.parameters?.model),
    subHeader: 'Generate text with OpenAI GPT models.',
    nodeColor: 'green',
  },
  anthropic: {
    title: 'Anthropic',
    icon: AnthropicIcon,
    statusChecker: (data: any) => Boolean(data?.parameters?.model),
    subHeader: 'Generate text with Anthropic Claude.',
    nodeColor: 'orange',
  },
  // gmailNode removed - duplicate of gmail
  // slackNode removed - duplicate of slack
  switch: {
    title: 'Switch',
    icon: SwitchIcon,
    statusChecker: (data: any) => {
      const params = data?.parameters;
      // Check for field AND at least one case or default case
      return Boolean(params?.field && (params?.cases?.length > 0 || params?.defaultCase));
    },
    subHeader: 'Route workflow based on field values with multiple conditions.',
    nodeColor: 'indigo',
    // Note: hideHandleBottom removed - switch nodes have multiple dynamic output handles
  },
  codeNode: {
    title: 'Code',
    icon: CodeIcon,
    statusChecker: (data: any) => Boolean(data?.parameters?.code?.trim()),
    subHeader: 'Run custom JavaScript on workflow data.',
    nodeColor: 'purple',
  },
  defineVariablesNode: {
    title: 'Define Variables',
    icon: DefineVarsIcon,
    statusChecker: (data: any) => {
      const vars = data?.parameters?.variables;
      return Array.isArray(vars) && vars.some((v: any) => v?.name?.trim());
    },
    subHeader: 'Define named variables for use in downstream nodes.',
    nodeColor: 'indigo',
  },
};

export const DesiredNodeOrder = [
  'httpNode',
  'webhookNode',
  'conditionNode',
  'mergeNode',
  'switch',
  'googleSheetNode',
  'llmNode',
  'hpmNode',
  // 'gmailNode' removed - duplicate
  // 'slackNode' removed - duplicate
  'openai',
  'anthropic',
  'enrichDomainNode',
  'moveToTALNode',
  'moveToAccountTALNode',
  'criteriaAccountNode',
  'criteriaContactNode',
  'sourceContactNode',
  'enrichContactNode',
  'assignContactNode',
  'addToCampaignNode',
  'accountCreatedNode',
  'accountUpdatedNode',
  'contactCreatedNode',
  'contactUpdatedNode',
  'codeNode',
  'defineVariablesNode',
];

export type NodesDataKeysType = keyof typeof NODES_DATA;
export const nodesData = NODES_DATA as Record<NodesDataKeysType, NodeData>;

export const getNodeTitle = (nodeType: string) => nodesData[nodeType]?.title || 'Node';

export const getNodeStatus = (node: Node) => {
  const data = node.data as Record<string, unknown>;
  const checker = nodesData[node.type as string]?.statusChecker;
  return checker ? (checker(data) ? 'Configured' : 'Not configured') : 'Not configured';
};
const filterNodesTypes = ['criteriaContactNode', 'criteriaAccountNode'];

export const getFiltersFromNode = (nodes: any[]): any[] => {
  const filterNode = nodes.find((node) => filterNodesTypes.includes(node.type));
  if (!filterNode) return [];
  return filterNode.data?.parameters?.filters?.value || {};
};

export const getCreateListPayload = (data: any): any => {
  const payload: any = {
    playbookName: data.playbookName,
    status: data.status,
    filters: data.filters?.length > 0 ? data.filters : [{}],
    tenant_id: data.tenant_id,
  };
  const nodes: Node[] = data?.nodes;
  nodes.forEach((node: Node) => {
    switch (node.type) {
      case 'assignContactNode':
        if (node.data?.selectedOwners && Array.isArray(node.data.selectedOwners)) {
          payload['userEmails'] = node.data.selectedOwners.map((owner: any) => owner.value);
        }
        break;
      // addToCampaignNode case removed - duplicate of addToCampaign
      default:
        break;
    }
  });
  return payload;
};

export const templateTypeOptions = {
  'Account Updated': [
    { label: 'AI Signal Enriched', value: 'AI Signal is enriched' },
    { label: 'Firmographic Data Change', value: 'FIRMOGRAPHIC_DATA_CHANGED' },
    { label: 'Tech Stack Enriched', value: 'Tech Stack Changed' },
    { label: 'Website Visit', value: 'Website visits' },
    { label: 'Intent Signal Changed', value: 'Intent Signal Changed' },
    { label: 'Add to List', value: 'ACCOUNT_ADDED_TO_LIST' }
  ],
  'Account Created': [{ label: 'Account Added to TAL', value: 'ACCOUNT_ADDED_TO_TAL' }],
  'Contact Created': [
    { label: 'Contact Added to TAL', value: 'CONTACT_ADDED_TO_TAL' },
    { label: 'Lead Added to TAL', value: 'LEAD_ADDED_TO_TAL' },
    { label: 'WAL Account Reveal', value: 'WAL_ACCOUNT_REVEAL' },
  ],
  'Contact Updated': [{ label: 'Contact Updated', value: 'CONTACT_UPDATED' }, { label: 'Add to List', value: 'CONTACT_ADDED_TO_LIST' }],
};

export const isNodeExistsForListCreation = (
  nodes: any[],
  playbookName: string,
  playbookId: string,
) => {
  const payloadData: any = {
    listName: playbookName,
    fePlaybookId: playbookId,
  };
  let isNodeExists = false;

  nodes?.forEach((node: any) => {
    if (['addToCampaign', 'assignContactNode', 'hpmNode'].includes(node.data.nodeType)) {
      isNodeExists = true;
    }

    if (node.data.nodeType === 'addToCampaign') {
      let sequenceData;
      try {
        sequenceData = JSON.parse(node.data.selectedSequence);
      } catch (e) {
        sequenceData = {};
      }

      payloadData.sequenceId = sequenceData.id ?? null;
      payloadData.name = sequenceData.name || '';
      payloadData.externalSequenceId = sequenceData.externalSequenceId || '';
      payloadData.sourceCmpName = sequenceData.sourceCmpName || '';
      payloadData.mailboxId = node.data.selectedMailBox || '';
      payloadData.pushToSequence = true;
    }

    if (node.data.nodeType === 'assignContactNode') {
      payloadData.userEmails = node.data.selectedOwners?.map((owner: any) => {
        return owner.value || '';
      });
    }
  });

  return {
    isNodeExists,
    payloadData,
  };
};
